// This script relies on global variables/functions typically provided by Shopify's Dawn theme,
// such as `debounce`, `ON_CHANGE_DEBOUNCE_TIMER`, `PUB_SUB_EVENTS`, `subscribe`, `routes`, `fetchConfig`, `CartPerformance`, `window.quickOrderListStrings`, `window.cartStrings`.
// If any of these are undefined after pasting, you might need to copy their definitions from your theme's `assets/global.js` or `theme.js` files.

// --- START: NEW/UPDATED GLOBAL UTILITY FUNCTIONS FOR CART.JS ---

// Utility function to get numeric ID from GID
function getNumericIdFromShopifyGid(gid) {
    if (!gid || typeof gid !== 'string') return null;
    const match = gid.match(/ProductVariant\/(\d+)/);
    return match ? parseInt(match[1]) : null;
}

// Utility function to format price from cents to currency string
function formatPrice(valueInCents) {
    const n = Number(valueInCents);
    return isNaN(n) ? "$0.00" : `$${(n / 100).toFixed(2)}`;
}

// Storefront API Access Token for GraphQL queries.
const STOREFRONT_API_ACCESS_TOKEN = 'e6da23bc86ba9bddff2257ec794267fc'; // <<< REPLACE WITH YOUR ACTUAL TOKEN <<<

// Function to make Storefront API GraphQL queries
async function storefrontApiQuery(query, variables = {}) {
    if (!STOREFRONT_API_ACCESS_TOKEN || STOREFRONT_API_ACCESS_TOKEN === 'YOUR_STOREFRONT_API_ACCESS_TOKEN') {
        console.error("Storefront API Token missing or placeholder. Cannot fetch product details.");
        throw new Error("Storefront API Token missing in cart.js config!");
    }
    const url = `/api/2024-04/graphql.json`;
    let response; // Declare response outside try to access in catch
    try {
        const payload = { query, variables };
        console.log('[storefrontApiQuery in cart.js] Sending Payload:', JSON.stringify(payload)); // Log the exact payload

        response = await fetch(url, { // Assign to outer response
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': STOREFRONT_API_ACCESS_TOKEN
            },
            body: JSON.stringify(payload)
        });

        // Check response status before trying to parse as JSON
        if (!response.ok) {
            // If not OK, log status and try to get text() for more info
            const errorText = await response.text();
            console.error(`[storefrontApiQuery in cart.js] GraphQL request FAILED. Status: ${response.status}. Response Text:`, errorText);
            throw new Error(`GraphQL HTTP Error: ${response.status}. Response: ${errorText}`);
        }

        // If response.ok, then try to parse
        const jsonResponse = await response.json(); // This is where the "Parse error on ..." happens

        // This part below will only be reached if response.json() was successful
        if (jsonResponse.errors) { // Check for GraphQL specific errors in the JSON
            const errorMessage = jsonResponse.errors.map(e => e.message).join(', ');
            console.error(`[storefrontApiQuery in cart.js] GraphQL API query returned errors. Errors:`, jsonResponse.errors);
            throw new Error(`GraphQL API query failed in cart.js: ${errorMessage}`);
        }
        if (!jsonResponse.data && !jsonResponse.errors) {
            console.warn("[storefrontApiQuery in cart.js] GraphQL query successful but no data field in cart.js response.");
            return null;
        }
        return jsonResponse.data;

    } catch (error) { // Catches errors from fetch, response.json(), or thrown errors
        console.error("[storefrontApiQuery in cart.js] Overall CATCH block error:", error.message);
        // If 'response' is defined and it's a parsing error, it means response.ok might have been true
        // but the body wasn't JSON. This is less common if status was 200.
        if (error.name === 'SyntaxError' && response) { // Specifically for JSON.parse errors
            console.error("[storefrontApiQuery in cart.js] SyntaxError (likely non-JSON response). Status was:", response.status);
            // Try to get raw text again if not already captured (might fail if stream used)
            // const rawErrorText = await response.text().catch(e => "Failed to get raw text again");
            // console.error("[storefrontApiQuery in cart.js] Raw response text that failed to parse:", rawErrorText);
        }
        // Re-throw the original error to be caught by the caller
        throw error;
    }
}
function getDisplayName(type) {
    const typeLower = type.toLowerCase();
    if (typeLower === 'rim') return "Rims";
    if (typeLower === 'hub') return "Hubs";
    if (typeLower === 'valvestem') return "Valve Stems";
    if (typeLower === 'nipples') return "Nipples";
    if (typeLower === 'spokes') return "Spokes";
    // Fallback for any other types
    return type.charAt(0).toUpperCase() + type.slice(1);
}

// --- START: FINAL, CONSISTENT REPLACEMENT for getDisplayNameForMainComponent ---
function getDisplayNameForMainComponent(type, details, buildState) {
    if (!details || !details.product) return '';
    
    // Step 1: Construct the base title with its variant options (e.g., color, spoke count).
    // This preserves the original parentheses.
    let finalTitle = details.product.title;
    if (details.title && details.title.toLowerCase() !== 'default title') {
        const variantPart = details.selectedOptions.map(o => o.value).join(' / ');
        finalTitle += ` - ${variantPart}`;
    }

    // Step 2: If this is the rear hub, simply append the freehub type to the very end.
    if (type === 'Hub' && buildState?.components?.rearHub?.variantId === details.id) {
        const freehubType = buildState.components.rearHub.spec_freehub;
        if (freehubType) {
            finalTitle += ` - ${freehubType}`;
        }
    }
    
    return finalTitle;
}
// --- END: FINAL, CONSISTENT REPLACEMENT for getDisplayNameForMainComponent ---

const TUBELESS_TAPE_PRODUCT_VARIANT_GID = 'gid://shopify/ProductVariant/51561220669747';
// --- END: NEW/UPDATED GLOBAL UTILITY FUNCTIONS FOR CART.JS ---

// In cart.js

async function revalidateAccessoryBundlesAgainstMainBuilds(currentCartItems) {
    console.log("[CartAccessoriesRevalidation] FUNCTION CALLED. Starting revalidation..."); // Log 1
    let cartWasModified = false;
    const updatesForCart = {};
    let removedAccessoriesNotifications = [];

    if (!currentCartItems || currentCartItems.length === 0) {
        console.log("[CartAccessoriesRevalidation] No currentCartItems passed or cart is empty. Exiting."); // Log 2
        return { cartUpdated: false };
    }

    const mainBuildPlaceholders = currentCartItems.filter(item =>
        item.properties && item.properties._build && item.properties._is_custom_wheel_build === "true"
    );

    if (mainBuildPlaceholders.length === 0) {
        console.log("[CartAccessoriesRevalidation] No main wheel builds found in cart. Exiting."); // Log 3
        return { cartUpdated: false };
    }
    console.log(`[CartAccessoriesRevalidation] Found ${mainBuildPlaceholders.length} main build placeholder(s).`); // Log 4

    for (const mainBuildPlaceholder of mainBuildPlaceholders) {
        const mainBuildId = mainBuildPlaceholder.properties._BuildID; // Corrected case
        if (!mainBuildId) {
            console.warn("[CartAccessoriesRevalidation] Main build placeholder missing _BuildID (checked with correct case).", mainBuildPlaceholder.properties);
            continue;
        }
        console.log(`[CartAccessoriesRevalidation] Processing main build ID: ${mainBuildId}`); // Log 5

        const editFlagKey = `ll_main_build_edited_${mainBuildId}`;
        const editFlagValue = sessionStorage.getItem(editFlagKey);
        console.log(`[CartAccessoriesRevalidation] Checking flag: ${editFlagKey}, Value: ${editFlagValue}`); // Log 6

        if (!editFlagValue) {
            console.log(`[CartAccessoriesRevalidation] Build ${mainBuildId} was NOT flagged as recently edited. Skipping accessory revalidation for it.`); // Log 7
            continue;
        }
        console.log(`[CartAccessoriesRevalidation] Build ${mainBuildId} WAS flagged. Proceeding with accessory revalidation.`); // Log 8

        let mainBuildSpecs;
        try {
            console.log(`[CartAccessoriesRevalidation] Raw _build property for ${mainBuildId}:`, mainBuildPlaceholder.properties._build); // Log 9
            mainBuildSpecs = JSON.parse(mainBuildPlaceholder.properties._build);
            console.log(`[CartAccessoriesRevalidation] Parsed mainBuildSpecs for ${mainBuildId}:`, JSON.parse(JSON.stringify(mainBuildSpecs))); // Log 10
        } catch (e) {
            console.error(`[CartAccessoriesRevalidation] Failed to parse _build JSON for main build ${mainBuildId}:`, e);
            sessionStorage.removeItem(editFlagKey);
            continue;
        }

        const accessoryBundleBuildId = mainBuildId + '-ACC';
        const individualAccessoriesInBundle = currentCartItems.filter(item =>
            item.properties && item.properties._parent_build_id === accessoryBundleBuildId && item.quantity > 0
        );

        if (individualAccessoriesInBundle.length === 0) {
            console.log(`[CartAccessoriesRevalidation] No individual accessories found for bundle ${accessoryBundleBuildId}.`); // Log 11
            sessionStorage.removeItem(editFlagKey);
            continue;
        }
        console.log(`[CartAccessoriesRevalidation] Found ${individualAccessoriesInBundle.length} accessories in bundle ${accessoryBundleBuildId}.`); // Log 12

        const getSpec = (pos, specType) => {
            const componentKey = `${pos}${specType}`;
            const component = mainBuildSpecs.components?.[componentKey];
            // console.log(`[CartReval - getSpec] Called for: ${pos} ${specType}. Component found in mainBuildSpecs: ${!!component}`);
            // if (component) {
            //     console.log(`[CartReval - getSpec] Component data for ${componentKey}:`, JSON.parse(JSON.stringify(component)));
            // }
            if (!component) return null;
            switch (specType) {
                case 'Rim':
    const wsValue = component.wheelSize;
    // Normalize to always be a string for comparison, assuming single selection for a wheel
    const wsString = Array.isArray(wsValue) ? wsValue[0] : wsValue;
    const iw = component.internal_width_mm;
    const ed = component.rimDepth;
    console.log(`[CartReval - getSpec - Rim] For ${pos}: ws='${wsString}', iw='${iw}', ed='${ed}'`);
    return { wheel_size: wsString, internal_width_mm: iw, effective_depth_mm: ed };
                case 'Hub':
                    const bi = component.spec_brake_interface;
                    return { spec_brake_interface: bi };
                default: return null;
            }
        };

        const rearRimSpecResult = getSpec('rear', 'Rim');
        // console.log(`[CartReval] Result from getSpec('rear', 'Rim'):`, JSON.parse(JSON.stringify(rearRimSpecResult || {})));

        const mainBuildDataForFiltering = {
            Front: {
                wheelSize: getSpec('front', 'Rim')?.wheel_size,
                rimInternalWidth: getSpec('front', 'Rim')?.internal_width_mm,
                rimEffectiveDepth: getSpec('front', 'Rim')?.effective_depth_mm,
                hubBrakeInterface: getSpec('front', 'Hub')?.spec_brake_interface
            },
            Rear: {
                wheelSize: rearRimSpecResult?.wheel_size,
                rimInternalWidth: rearRimSpecResult?.internal_width_mm,
                rimEffectiveDepth: rearRimSpecResult?.effective_depth_mm,
                hubBrakeInterface: getSpec('rear', 'Hub')?.spec_brake_interface
            }
        };
        console.log(`[CartAccessoriesRevalidation] mainBuildDataForFiltering for ${mainBuildId}:`, JSON.parse(JSON.stringify(mainBuildDataForFiltering))); // Log 13

        if (mainBuildSpecs.buildType === 'Wheel Set') {
            if (!mainBuildDataForFiltering.Front?.wheelSize || mainBuildDataForFiltering.Front?.rimInternalWidth === undefined ||
                !mainBuildDataForFiltering.Rear?.wheelSize  || mainBuildDataForFiltering.Rear?.rimInternalWidth === undefined) {
                console.warn(`[CartAccessoriesRevalidation] Critical specs (wheelSize/rimInternalWidth) missing for Wheel Set build ${mainBuildId}. Cannot reliably revalidate accessories.`, mainBuildDataForFiltering);
                sessionStorage.removeItem(editFlagKey);
                continue;
            }
        } else if (mainBuildSpecs.buildType === 'Front') {
             if (!mainBuildDataForFiltering.Front?.wheelSize || mainBuildDataForFiltering.Front?.rimInternalWidth === undefined) {
                console.warn(`[CartAccessoriesRevalidation] Critical specs (wheelSize/rimInternalWidth) missing for Front Wheel build ${mainBuildId}.`, mainBuildDataForFiltering.Front);
                sessionStorage.removeItem(editFlagKey);
                continue;
            }
        } else if (mainBuildSpecs.buildType === 'Rear') {
             if (!mainBuildDataForFiltering.Rear?.wheelSize || mainBuildDataForFiltering.Rear?.rimInternalWidth === undefined) {
                console.warn(`[CartAccessoriesRevalidation] Critical specs (wheelSize/rimInternalWidth) missing for Rear Wheel build ${mainBuildId}.`, mainBuildDataForFiltering.Rear);
                sessionStorage.removeItem(editFlagKey);
                continue;
            }
        }

        const accessoryVariantGidsToFetch = individualAccessoriesInBundle.map(item => `gid://shopify/ProductVariant/${item.variant_id}`);
        if (accessoryVariantGidsToFetch.length === 0) {
            sessionStorage.removeItem(editFlagKey);
            continue;
        }
        
        const accessoryDetailsQuery = `
            query getAccessoryVariantDetails($ids: [ID!]!) {
                nodes(ids: $ids) {
                    ... on ProductVariant {
                        id
                        title
                        wheelSpecRimSize: metafield(namespace:"custom", key:"wheel_spec_rim_size") { value }
                        compatibleRimWidthMin: metafield(namespace:"custom", key:"accessory_compatible_rim_width_min") { value }
                        compatibleRimWidthMax: metafield(namespace:"custom", key:"accessory_compatible_rim_width_max") { value }
                        valveMinRimDepth: metafield(namespace:"custom", key:"accessory_valve_min_effective_rim_depth_mm") { value }
                        valveMaxRimDepth: metafield(namespace:"custom", key:"accessory_valve_max_effective_rim_depth_mm") { value }
                        # Add other metafields like wheel_spec_brake_interface here if needed
                    }
                }
            }
        `;
     
        let fetchedAccessoryVariantsData;
        try {
            fetchedAccessoryVariantsData = await storefrontApiQuery(accessoryDetailsQuery, { ids: accessoryVariantGidsToFetch });
            console.log(`[CartAccessoriesRevalidation] GraphQL response for accessory metafields:`, JSON.parse(JSON.stringify(fetchedAccessoryVariantsData || {}))); // Log 16
        } catch (gqlError) {
            console.error(`[CartAccessoriesRevalidation] GraphQL query for accessory metafields FAILED for build ${mainBuildId}:`, gqlError);
            sessionStorage.removeItem(editFlagKey);
            continue;
        }
        
        const accessoryMetafieldsMap = new Map();
        fetchedAccessoryVariantsData?.nodes?.forEach(node => {
            if (node) {
    // console.log("[CartAccessoriesRevalidation] Node Data from REAL query:", JSON.parse(JSON.stringify(node)));
    accessoryMetafieldsMap.set(node.id, {
            wheelSpecRimSize: node.wheelSpecRimSize ? JSON.parse(node.wheelSpecRimSize.value || "[]") : [],
            compatibleRimWidthMin: node.compatibleRimWidthMin ? parseInt(node.compatibleRimWidthMin.value) : null,
            compatibleRimWidthMax: node.compatibleRimWidthMax ? parseInt(node.compatibleRimWidthMax.value) : null,
            valveMinRimDepth: node.valveMinRimDepth ? parseInt(node.valveMinRimDepth.value) : null,
            valveMaxRimDepth: node.valveMaxRimDepth ? parseInt(node.valveMaxRimDepth.value) : null,
            // Parse other metafields you added to the query
        });
}
});

        let currentBundleRemovedItems = [];
        for (const accItem of individualAccessoriesInBundle) {
            const variantGid = `gid://shopify/ProductVariant/${accItem.variant_id}`;
            const accessoryVariantMetafields = accessoryMetafieldsMap.get(variantGid);
            const positionContext = accItem.properties._position_context || 'general';
            const category = accItem.properties._category || '';

            // --- LOG INSERTION POINT 1 ---
            console.log(`[CartAccessoriesRevalidation] CHECKING ACCESSORY: "${accItem.properties._display_name}", Position: ${positionContext}, Category: ${category}, Variant GID: ${variantGid}`);
            console.log(`[CartAccessoriesRevalidation] Using (dummy) metafields for ${variantGid}:`, JSON.parse(JSON.stringify(accessoryVariantMetafields || {})));
            // ---

            if (!accessoryVariantMetafields) {
                console.warn(`[CartAccessoriesRevalidation] Metafields (dummy or real) not found for accessory variant ${variantGid}. Assuming Incompatible.`);
                updatesForCart[accItem.key] = 0;
                cartWasModified = true;
                currentBundleRemovedItems.push(accItem.properties._display_name || accItem.title || `Item ID ${accItem.variant_id}`);
                continue;
            }

            let isCompatible = true;
            let targetSpec; // DECLARED HERE

            // Determine targetSpec based on accessory's positionContext
            if (positionContext === 'Front') {
                targetSpec = mainBuildDataForFiltering.Front;
            } else if (positionContext === 'Rear') {
                targetSpec = mainBuildDataForFiltering.Rear;
            } else { // 'general'
                if (mainBuildSpecs.buildType === 'Wheel Set') {
                    targetSpec = mainBuildDataForFiltering.Front;
                } else if (mainBuildSpecs.buildType === 'Front') {
                    targetSpec = mainBuildDataForFiltering.Front;
                } else if (mainBuildSpecs.buildType === 'Rear') {
                    targetSpec = mainBuildDataForFiltering.Rear;
                } else {
                    console.warn(`[CartAccessoriesRevalidation] Unknown mainBuildSpecs.buildType: '${mainBuildSpecs.buildType}' for 'general' accessory. Setting targetSpec to null.`);
                    targetSpec = null;
                }
            }

            // --- LOG INSERTION POINT 2 (MOVED HERE) ---
            console.log(`[CartAccessoriesRevalidation] Target main build specs for "${accItem.properties._display_name}" (Pos: ${positionContext}):`, JSON.parse(JSON.stringify(targetSpec || {})));
            // ---

            // Compatibility check for missing targetSpec or fundamental properties
            if (isCompatible && (!targetSpec || targetSpec.wheelSize === undefined)) {
                if ((positionContext === 'Rear' && mainBuildSpecs.buildType === 'Front') ||
                    (positionContext === 'Front' && mainBuildSpecs.buildType === 'Rear')) {
                    console.log(`[CartAccessoriesRevalidation] Incompatible: Accessory ${accItem.properties._display_name} for ${positionContext}, but main build is ${mainBuildSpecs.buildType} only.`);
                    isCompatible = false;
                } else if (!targetSpec && (category === 'Tires' || category === 'Tire Inserts')) {
                     console.warn(`[CartAccessoriesRevalidation] CRITICAL: targetSpec is null/undefined for ${positionContext} on build ${mainBuildId}. Cannot validate ${category}. Assuming incompatible.`);
                     isCompatible = false;
                } else if (targetSpec && targetSpec.wheelSize === undefined && (category === 'Tires' || category === 'Tire Inserts')) {
                    console.warn(`[CartAccessoriesRevalidation] Target wheel size missing in targetSpec for ${positionContext} on build ${mainBuildId}. Accessory "${accItem.properties._display_name || accItem.title}" for category ${category} deemed incompatible.`);
                    isCompatible = false;
                }
            }

            // Wheel Size Check
            if (isCompatible && targetSpec && targetSpec.wheelSize && accessoryVariantMetafields.wheelSpecRimSize?.length > 0) {
    // targetSpec.wheelSize from _build will be like "29\"" or ["27.5\""]
    const targetWheelSizeForCheck = Array.isArray(targetSpec.wheelSize) ? targetWheelSizeForCheck[0] : targetSpec.wheelSize;

    if (targetWheelSizeForCheck && !accessoryVariantMetafields.wheelSpecRimSize.includes(targetWheelSizeForCheck)) {
        console.log(`[CartAccessoriesRevalidation] Incompatible by Wheel Size: "${accItem.properties._display_name}". Accessory supports [${accessoryVariantMetafields.wheelSpecRimSize.join(', ')}], Main build has ${targetWheelSizeForCheck} for ${positionContext}.`);
        isCompatible = false;
    }
}

            // Rim Internal Width Check
            if (isCompatible && (category === 'Tires' || category === 'Tire Inserts') && targetSpec && targetSpec.rimInternalWidth !== null && targetSpec.rimInternalWidth !== undefined) {
                const minW = accessoryVariantMetafields.compatibleRimWidthMin;
                const maxW = accessoryVariantMetafields.compatibleRimWidthMax;
                if (minW !== null && maxW !== null) {
                    if (targetSpec.rimInternalWidth < minW || targetSpec.rimInternalWidth > maxW) {
                        console.log(`[CartAccessoriesRevalidation] Incompatible by Rim Width: "${accItem.properties._display_name}". Accessory range ${minW}-${maxW}mm, Main build ${positionContext} rim is ${targetSpec.rimInternalWidth}mm.`);
                        isCompatible = false;
                    }
                }
            }
            
            // Rim Effective Depth Check
            if (isCompatible && category === 'Tire Inserts' && targetSpec && targetSpec.rimEffectiveDepth !== null && targetSpec.rimEffectiveDepth !== undefined) {
                const minD = accessoryVariantMetafields.valveMinRimDepth;
                const maxD = accessoryVariantMetafields.valveMaxRimDepth;
                if (minD !== null && maxD !== null) {
                    if (targetSpec.rimEffectiveDepth < minD || targetSpec.rimEffectiveDepth > maxD) {
                        console.log(`[CartAccessoriesRevalidation] Incompatible by Rim Depth (for valve): "${accItem.properties._display_name}". Accessory valve range ${minD}-${maxD}mm, Main build ${positionContext} effective rim depth is ${targetSpec.rimEffectiveDepth}mm.`);
                        isCompatible = false;
                    }
                }
            }

            // --- LOG INSERTION POINT 3 ---
            if (!isCompatible) {
                console.log(`[CartAccessoriesRevalidation] <<< FINAL DECISION: INCOMPATIBLE >>> Accessory: "${accItem.properties._display_name}" (Key: ${accItem.key}). Adding to updatesForCart.`);
                updatesForCart[accItem.key] = 0;
                cartWasModified = true;
                currentBundleRemovedItems.push(accItem.properties._display_name || accItem.title || `Item ID ${accItem.variant_id}`);
            } else {
                console.log(`[CartAccessoriesRevalidation] <<< FINAL DECISION: COMPATIBLE >>> Accessory: "${accItem.properties._display_name}" (Key: ${accItem.key}).`);
            }
            // ---
        } // End of loop for each accItem

        if (currentBundleRemovedItems.length > 0) {
            removedAccessoriesNotifications.push({
                buildIdForNotification: mainBuildId,
                removedItems: currentBundleRemovedItems
            });
        }
        console.log(`[CartAccessoriesRevalidation] Finished processing accessories for build ${mainBuildId}. Clearing edit flag.`); // Log 17
        sessionStorage.removeItem(editFlagKey);
    } // End of mainBuildPlaceholders loop

    console.log(`[CartAccessoriesRevalidation] Finished looping through all main builds. cartWasModified: ${cartWasModified}`); // Log 18
    if (cartWasModified) {
        if (removedAccessoriesNotifications.length > 0) {
            sessionStorage.setItem('ll_removed_accessories_info', JSON.stringify(removedAccessoriesNotifications));
            console.log("[CartAccessoriesRevalidation] Stored removed accessories notifications in sessionStorage:", removedAccessoriesNotifications); // Log 19
        }
        console.log("[CartAccessoriesRevalidation] Returning with cartUpdated: true, Updates:", JSON.parse(JSON.stringify(updatesForCart))); // Log 20
        return { cartUpdated: true, updates: updatesForCart };
    } else {
        console.log("[CartAccessoriesRevalidation] No incompatible accessories found or no bundles needed revalidation. Returning cartUpdated: false."); // Log 21
        return { cartUpdated: false };
    }
} // End of revalidateAccessoryBundlesAgainstMainBuilds

function displayCartLevelAccessoryNotification() {
    const notificationContainer = document.getElementById('cart-level-accessory-notification');
    if (!notificationContainer) {
        // console.warn("[displayCartLevelAccessoryNotification] Container #cart-level-accessory-notification not found.");
        return;
    }

    const removedInfoString = sessionStorage.getItem('ll_removed_accessories_info');
    // console.log("[displayCartLevelAccessoryNotification] Checked sessionStorage 'll_removed_accessories_info':", removedInfoString);

    if (removedInfoString) {
        try {
            const allRemovedNotifications = JSON.parse(removedInfoString);
            let combinedMessageHtml = '';
            let hasContentForDisplay = false;

            allRemovedNotifications.forEach(notificationEntry => {
                if (notificationEntry.removedItems && notificationEntry.removedItems.length > 0) {
                    hasContentForDisplay = true;
                    combinedMessageHtml += `<p style="margin-bottom: 5px;">For your <strong>Custom Wheel Build (ID: ${notificationEntry.buildIdForNotification})</strong>, the following accessories were removed as they are no longer compatible:</p><ul style="margin-top: 0; margin-bottom: 10px;">`;
                    notificationEntry.removedItems.forEach(itemDisplayName => {
                        combinedMessageHtml += `<li style="margin-left: 20px;">${itemDisplayName}</li>`;
                    });
                    combinedMessageHtml += `</ul>`;
                }
            });

            if (hasContentForDisplay) {
                const relevantBuildId = allRemovedNotifications.find(n => n.removedItems && n.removedItems.length > 0)?.buildIdForNotification;
                const accessoriesPageUrl = relevantBuildId ? `/pages/accessories?build_id=${relevantBuildId}&edit_accessories=true` : '#';
                
                combinedMessageHtml += `<p><a href="${accessoriesPageUrl}" class="button button--secondary">Review Accessories</a></p>`;
                
                notificationContainer.innerHTML = combinedMessageHtml;
                notificationContainer.style.display = 'block'; // Or 'flex' or whatever your notification style is
                console.log("[displayCartLevelAccessoryNotification] Displayed notification on cart page.");

                // Optional: Clear the sessionStorage key *after displaying it on the cart page*
                // This means it will only show once on the cart page.
                // If you also want it on the accessories page, accessories-builder.js needs to handle its own clearing.
                // For now, let's clear it here to avoid it showing on subsequent non-related cart refreshes.
                // sessionStorage.removeItem('ll_removed_accessories_info');
                // console.log("[displayCartLevelAccessoryNotification] Cleared 'll_removed_accessories_info' from sessionStorage after display.");

            } else {
                notificationContainer.style.display = 'none';
                notificationContainer.innerHTML = '';
                // console.log("[displayCartLevelAccessoryNotification] No actual items to notify about, hiding container.");
            }
        } catch (e) {
            console.error("[displayCartLevelAccessoryNotification] Error parsing sessionStorage:", e);
            notificationContainer.style.display = 'none';
            notificationContainer.innerHTML = '';
        }
    } else {
        notificationContainer.style.display = 'none';
        notificationContainer.innerHTML = '';
        // console.log("[displayCartLevelAccessoryNotification] No 'll_removed_accessories_info' found in sessionStorage.");
    }
}

async function updateAccessoryBundlePlaceholders(currentCartItems, cartItemsInstance) {
    console.log("[UpdateAccPlaceholders] Starting update of accessory bundle placeholders.");
    let anApiUpdateWasAttempted = false;

    if (!currentCartItems) {
        console.warn("[UpdateAccPlaceholders] currentCartItems is null or undefined. Cannot proceed.");
        return false;
    }

    const accessoryPlaceholders = currentCartItems.filter(item =>
        item.properties && item.properties._is_custom_accessories_bundle === "true" && item.quantity > 0
    );

    if (accessoryPlaceholders.length === 0) {
        return false;
    }

    for (const placeholder of accessoryPlaceholders) {
        const bundleId = placeholder.properties._build_id;
        if (!bundleId) {
            console.warn("[UpdateAccPlaceholders] Placeholder found without _build_id property:", placeholder);
            continue;
        }

        const individualAccessories = currentCartItems.filter(item =>
            item.properties && item.properties._parent_build_id === bundleId && item.quantity > 0 &&
            !item.properties._is_custom_accessories_bundle // <-- THIS IS THE NEW LINE
        );

        let newTotalPriceCents = 0;
        let newItemCount = 0;
        individualAccessories.forEach(acc => {
            newTotalPriceCents += acc.final_line_price;
            newItemCount += acc.quantity;
        });

        const currentTotalPriceString = placeholder.properties._total_price || "0.00";
        const currentItemCountString = placeholder.properties._component_count || "0";

        const newTotalPriceFormatted = (newTotalPriceCents / 100).toFixed(2);
        const newItemCountFormatted = newItemCount.toString();

        let needsApiUpdate = false;
        if (newTotalPriceFormatted !== currentTotalPriceString) {
            needsApiUpdate = true;
        }
        if (newItemCountFormatted !== currentItemCountString) {
            needsApiUpdate = true;
        }

        if (needsApiUpdate) {
            let itemLineNumber = placeholder.line;

            // --- START: ROBUSTNESS FIX ---
            // If placeholder.line is missing (which is causing the error), this block
            // attempts to recover it by finding the item's index in the complete cart array.
            // The 'line' property should simply be the item's index + 1.
            if (!itemLineNumber && placeholder.key) {
                const itemIndex = currentCartItems.findIndex(item => item.key === placeholder.key);
                if (itemIndex !== -1) {
                    itemLineNumber = itemIndex + 1;
                    console.warn(`[UpdateAccPlaceholders] Recovery: Placeholder for bundle ${bundleId} was missing 'line' property. Manually calculated it as ${itemLineNumber}.`);
                }
            }
            // --- END: ROBUSTNESS FIX ---

            // We check again. If we still don't have a line number, we cannot proceed.
            if (!itemLineNumber) {
                console.error(`[UpdateAccPlaceholders] CRITICAL ERROR: The placeholder for bundle ${bundleId} is missing its 'line' property and it could not be recovered. Cannot safely update.`, placeholder);
                continue; // Skip to the next placeholder to prevent a crash.
            }

            console.log(`[UpdateAccPlaceholders] Preparing update for bundle ${bundleId} at unique line number: ${itemLineNumber}.`);

            const newProps = { ...placeholder.properties };
            newProps._total_price = newTotalPriceFormatted;
            newProps._component_count = newItemCountFormatted;
            newProps._timestamp = new Date().toISOString();

            // The payload now correctly uses the recovered 'line' number to target the specific item.
            const changePayload = {
                line: itemLineNumber,
                quantity: 1,
                properties: newProps
            };

            const updateUrl = routes.cart_change_url;

            try {
                console.log(`[UpdateAccPlaceholders] Sending API update to ${updateUrl} for placeholder at LINE: ${itemLineNumber}, Payload:`, JSON.parse(JSON.stringify(changePayload)));
                const response = await fetch(updateUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify(changePayload)
                });

                if (response.ok) {
                    const updatedCartJson = await response.json();
                    console.log(`[UpdateAccPlaceholders] Successfully updated placeholder for line ${itemLineNumber}.`);
                    anApiUpdateWasAttempted = true;
                    if (cartItemsInstance && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                         publish(PUB_SUB_EVENTS.cartUpdate, { source: 'UpdateAccPlaceholdersInternal', cartData: updatedCartJson });
                    }
                } else {
                    const errorData = await response.json().catch(() => response.text());
                    console.error(`[UpdateAccPlaceholders] Error updating placeholder at line ${itemLineNumber}. Status: ${response.status}. Error:`, errorData);
                }
            } catch (e) {
                console.error(`[UpdateAccPlaceholders] Network/fetch error updating placeholder at line ${itemLineNumber}:`, e);
            }
        }
    }

    return anApiUpdateWasAttempted;
}

// --- START: REPLACEMENT for fetchAndDisplayCustomBuildComponents (with buildState passing) ---
async function fetchAndDisplayCustomBuildComponents(buildMapString, containerElement) {
    if (!containerElement || !buildMapString) {
        if (containerElement) containerElement.innerHTML = '<p>Error: Build data missing.</p>';
        return;
    }
    containerElement.innerHTML = '<p>Loading components...</p>';

    try {
        const buildState = JSON.parse(buildMapString);
        if (!buildState || !buildState.components) {
            throw new Error("Build data is not in the expected format.");
        }

        const buildType = buildState.buildType;
        const allGidsToFetch = new Set();
        const fetchedDetailsMap = new Map();

        const componentRenderOrder = ['Rim', 'Hub', 'Spokes', 'Nipples', 'ValveStem'];
        const serviceRenderOrder = ['AssemblyService', 'TubelessTape', 'RequiredAddons'];
        const organizedBuild = {};

        const addGid = (gid) => { if (gid) allGidsToFetch.add(gid); };

        componentRenderOrder.forEach(type => {
            const frontComp = buildState.components[`front${type}`];
            const rearComp = buildState.components[`rear${type}`];
            if (frontComp || rearComp) {
                organizedBuild[type] = { front: frontComp, rear: rearComp };
                addGid(frontComp?.variantId);
                addGid(rearComp?.variantId);
            }
        });

        organizedBuild.Services = [];
        const frontServices = {};
        const rearServices = {};
        
        // ... (The service and addon GID collection logic remains the same, no changes needed here)
        serviceRenderOrder.forEach(type => {
            const frontServiceItemData = buildState.components[`front${type}`];
            const rearServiceItemData = buildState.components[`rear${type}`];
            if (frontServiceItemData) { if (Array.isArray(frontServiceItemData)) { frontServiceItemData.forEach(s => { if (!s || !s.variantGid) return; addGid(s.variantGid); frontServices[s.variantGid] = { ...s, type: type }; }); } else { if (!frontServiceItemData.variantGid) return; addGid(frontServiceItemData.variantGid); const serviceObject = { ...frontServiceItemData, type: type }; if (type === 'AssemblyService') serviceObject.nameOverride = 'Assembly Service'; if (type === 'TubelessTape') serviceObject.nameOverride = 'Tubeless Tape (Installed)'; frontServices[frontServiceItemData.variantGid] = serviceObject; } }
            if (rearServiceItemData) { if (Array.isArray(rearServiceItemData)) { rearServiceItemData.forEach(s => { if (!s || !s.variantGid) return; addGid(s.variantGid); rearServices[s.variantGid] = { ...s, type: type }; }); } else { if (!rearServiceItemData.variantGid) return; addGid(rearServiceItemData.variantGid); const serviceObject = { ...rearServiceItemData, type: type }; if (type === 'AssemblyService') serviceObject.nameOverride = 'Assembly Service'; if (type === 'TubelessTape') serviceObject.nameOverride = 'Tubeless Tape (Installed)'; rearServices[rearServiceItemData.variantGid] = serviceObject; } }
        });
        organizedBuild.Services = { front: Object.values(frontServices), rear: Object.values(rearServices) };
        Object.values(buildState.selectedOptionalAddons || {}).forEach(addon => { const baseComponent = organizedBuild[addon.baseComponentType]; if (baseComponent) { if (!baseComponent.addons) baseComponent.addons = {}; if (!baseComponent.addons[addon.addonProductId]) { baseComponent.addons[addon.addonProductId] = { front: null, rear: null, name: addon.addonName }; } if (addon.baseComponentPosition === 'Front') baseComponent.addons[addon.addonProductId].front = addon; if (addon.baseComponentPosition === 'Rear') baseComponent.addons[addon.addonProductId].rear = addon; addGid(addon.addonVariantId); } });

        if (allGidsToFetch.size > 0) {
            const query = `
                query getVariantsDetails($ids: [ID!]!) {
                    nodes(ids: $ids) {
                        ... on ProductVariant {
                            id title product { title } selectedOptions { name value }
                        }
                    }
                }`;
            const data = await storefrontApiQuery(query, { ids: Array.from(allGidsToFetch) });
            data?.nodes?.forEach(node => { if (node) fetchedDetailsMap.set(node.id, node); });
        }

        let finalHtml = '<ul class="consolidated-build-list">';

        // --- THE MAIN CHANGE IS HERE, IN THE getDisplay() FUNCTION DEFINITION ---
        const getDisplay = (gid, type) => {
            const details = fetchedDetailsMap.get(gid);
            // We now pass the type and the full buildState to the helper function
            return details ? getDisplayNameForMainComponent(type, details, buildState) : 'Unknown Component';
        };
        // --- END OF MAIN CHANGE ---

        // ... (The generateLineHtml and generateAddonHtml functions remain the same)
        const generateLineHtml = (prefix, name, gid, isAddon = false) => { const displayName = name; const addonClass = isAddon ? 'addon-text' : ''; return `<li><span class="component-name ${addonClass}"><span class="component-prefix">${prefix}</span>${displayName}</span></li>`; };
        const generateAddonHtml = (addons) => { if (!addons) return ''; let addonHtml = '<ul class="component-list-indented">'; Object.values(addons).forEach(addon => { const frontGid = addon.front?.addonVariantId; const rearGid = addon.rear?.addonVariantId; if (buildType === 'Wheel Set') { if (frontGid && frontGid === rearGid) { addonHtml += generateLineHtml('F/R (2) -', `Add-on: ${addon.name}`, frontGid, true); } else { if (frontGid) addonHtml += generateLineHtml('F -', `Add-on: ${addon.name}`, frontGid, true); if (rearGid) addonHtml += generateLineHtml('R -', `Add-on: ${addon.name}`, rearGid, true); } } else { const singleGid = frontGid || rearGid; if (singleGid) addonHtml += generateLineHtml('- ', `Add-on: ${addon.name}`, singleGid, true); } }); addonHtml += '</ul>'; return addonHtml; };

        componentRenderOrder.forEach(type => {
            const component = organizedBuild[type];
            if (!component) return;

            const frontGid = component.front?.variantId;
            const rearGid = component.rear?.variantId;
            const isWheelSet = buildType === 'Wheel Set';
            
            // This componentText logic is now much cleaner because getDisplay does the hard work
            let componentText = '';
            if (isWheelSet) {
                if (frontGid && rearGid && frontGid === rearGid) {
                    componentText = `F/R (2) - ${getDisplay(frontGid, type)}`;
                } else {
                    if (frontGid) { componentText += `F - ${getDisplay(frontGid, type)}`; }
                    if (rearGid) {
                        if (frontGid) { componentText += `<br>`; }
                        componentText += `R - ${getDisplay(rearGid, type)}`;
                    }
                }
            } else {
                const singleGid = frontGid || rearGid;
                if (singleGid) {
                    componentText = getDisplay(singleGid, type);
                }
            }

            if (componentText) {
                 finalHtml += `<li class="component-category-title"><strong>${getDisplayName(type)}:</strong></li>`;
                 finalHtml += `<li><span class="component-name">${componentText}</span>${generateAddonHtml(component.addons)}</li>`;
            }
        });

        // ... (The rest of the function for Services remains the same)
        const { front: frontServiceList, rear: rearServiceList } = organizedBuild.Services; const allServiceGids = new Set([...frontServiceList.map(s => s.variantGid), ...rearServiceList.map(s => s.variantGid)]); if (allServiceGids.size > 0) { finalHtml += `<li class="component-category-title"><strong>Assembly & Specialized Parts:</strong></li>`; const sortedServiceGids = Array.from(allServiceGids).sort((gidA, gidB) => { const serviceA = frontServiceList.find(s => s.variantGid === gidA) || rearServiceList.find(s => s.variantGid === gidA); const serviceB = frontServiceList.find(s => s.variantGid === gidB) || rearServiceList.find(s => s.variantGid === gidB); const sortOrder = { 'AssemblyService': 10, 'TubelessTape': 20, 'RequiredAddon': 30 }; return (sortOrder[serviceA.type] || 99) - (sortOrder[serviceB.type] || 99); }); sortedServiceGids.forEach(gid => { const inFront = frontServiceList.some(s => s.variantGid === gid); const inRear = rearServiceList.some(s => s.variantGid === gid); const service = frontServiceList.find(s => s.variantGid === gid) || rearServiceList.find(s => s.variantGid === gid); const name = service.nameOverride || service.name; if (name) { if (buildType === 'Wheel Set') { if (inFront && inRear) finalHtml += generateLineHtml('F/R (2) -', name, gid, false); else if (inFront) finalHtml += generateLineHtml('F -', name, gid, false); else if (inRear) finalHtml += generateLineHtml('R -', name, gid, false); } else { if (inFront || inRear) finalHtml += generateLineHtml('- ', name, gid, false); } } }); }

        finalHtml += '</ul>';
        containerElement.innerHTML = finalHtml;

    } catch (error) {
        console.error('FAD_CBC (New Version with buildState passing) ERROR:', error);
        containerElement.innerHTML = `<p>Error loading components: ${error.message}.</p>`;
    }
}
// --- END: REPLACEMENT for fetchAndDisplayCustomBuildComponents ---

async function fetchAndDisplayAccessoryBundleComponents(accessoryPlaceholderItem, allCartItems, containerElement) {
    if (!containerElement || !accessoryPlaceholderItem) {
        if(containerElement) containerElement.innerHTML = '<p>Error: FAD_ABC missing data.</p>';
        return;
    }
    containerElement.innerHTML = '<p>Loading accessories...</p>';

    try {
        const bundleBuildId = accessoryPlaceholderItem.properties._build_id;
                const associatedAccessories = allCartItems.filter(item =>
            item.properties &&
            item.properties._parent_build_id === bundleBuildId &&
            item.quantity > 0 &&
            !item.properties._is_custom_accessories_bundle // <-- THIS IS THE NEW LINE
        );

        if (associatedAccessories.length === 0) {
            containerElement.innerHTML = '<p>No accessories currently in this bundle.</p>';
            return;
        }

        const accessoriesByCategory = {};
        const categoryOrder = ['Tire Inserts', 'Tires', 'Rotors', 'Sealant', 'Rim Tape', 'Tools'];
        let foundCategories = new Set();

        associatedAccessories.forEach(item => {
            const category = item.properties._category || 'Other Accessories';
            if (!accessoriesByCategory[category]) {
                accessoriesByCategory[category] = [];
            }
            accessoriesByCategory[category].push(item);
            foundCategories.add(category);
        });

        const sortedCategoryNames = [...foundCategories].sort((a, b) => {
            const indexA = categoryOrder.indexOf(a);
            const indexB = categoryOrder.indexOf(b);
            const effectiveA = indexA === -1 ? Infinity : indexA;
            const effectiveB = indexB === -1 ? Infinity : indexB;
            if (effectiveA !== effectiveB) return effectiveA - effectiveB;
            return a.localeCompare(b);
        });

        let accessoriesHtml = '<ul class="consolidated-build-list">';

        sortedCategoryNames.forEach(category => {
            const itemsInCategory = accessoriesByCategory[category];
            
            accessoriesHtml += `<li class="component-category-title"><strong>${category}:</strong></li>`;
            
            // --- START: UNIFIED SORTING AND RENDERING LOGIC ---
            const groupedByDisplayName = {};
            itemsInCategory.forEach(item => {
                const displayName = item.properties._display_name || item.title;
                if (!groupedByDisplayName[displayName]) {
                    groupedByDisplayName[displayName] = { front: null, rear: null };
                }
                if (item.properties.Wheel === 'Front') {
                    groupedByDisplayName[displayName].front = item;
                } else if (item.properties.Wheel === 'Rear') {
                    groupedByDisplayName[displayName].rear = item;
                } else { // Handle 'General' or items with no position
                    if (!groupedByDisplayName[displayName].general) groupedByDisplayName[displayName].general = [];
                    groupedByDisplayName[displayName].general.push(item);
                }
            });

            Object.keys(groupedByDisplayName).forEach(displayName => {
                const itemGroup = groupedByDisplayName[displayName];
                const frontItem = itemGroup.front;
                const rearItem = itemGroup.rear;
                const generalItems = itemGroup.general;

                if (frontItem && rearItem && frontItem.variant_id === rearItem.variant_id) {
                    // Consolidate Front & Rear of the exact same item
                    const totalQty = frontItem.quantity + rearItem.quantity;
                    accessoriesHtml += `<li><span class="component-name"><span class="component-prefix">F/R (${totalQty}) -</span>${displayName}</span></li>`;
                } else {
                    // List Front and Rear separately if they exist
                    if (frontItem) {
                        accessoriesHtml += `<li><span class="component-name"><span class="component-prefix">F -</span>${displayName} (Qty: ${frontItem.quantity})</span></li>`;
                    }
                    if (rearItem) {
                        accessoriesHtml += `<li><span class="component-name"><span class="component-prefix">R -</span>${displayName} (Qty: ${rearItem.quantity})</span></li>`;
                    }
                }

                // List any general items
                if (generalItems) {
                    generalItems.forEach(item => {
                       accessoriesHtml += `<li><span class="component-name"><span class="component-prefix">- </span>${displayName} (Qty: ${item.quantity})</span></li>`;
                    });
                }
            });
            // --- END: UNIFIED SORTING AND RENDERING LOGIC ---
        });

        accessoriesHtml += '</ul>';
        containerElement.innerHTML = accessoriesHtml;

    } catch (e) {
        console.error("FAD_ABC (Unified Formatting Version) ERROR", e);
        if(containerElement) containerElement.innerHTML = '<p>Error loading accessories.</p>';
    }
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', async (event) => {
      event.preventDefault();
      const cartItemsContainer = this.closest('cart-items') || this.closest('cart-drawer-items');
      const bundleType = this.dataset.bundleType;

      // --- Logic for Main Wheel Builder and Accessory Bundles (Unchanged) ---
      if (bundleType === 'main-build' || bundleType === 'accessory-bundle') {
        const isMainBuild = bundleType === 'main-build';
        const keysString = this.dataset.keysToRemove;
        const mainBuildId = this.dataset.buildId || this.dataset.mainBuildId;

        if (!keysString) {
          console.error(`Remove Error (${bundleType}): Missing 'data-keys-to-remove' attribute.`, this.dataset);
          return;
        }

        const confirmationMessage = isMainBuild
          ? "Are you sure you want to remove this wheel build? This will also remove its associated accessory bundle."
          : "Are you sure you want to remove this accessory bundle?";

        if (!confirm(confirmationMessage)) return;

        if (mainBuildId) {
          this.cleanupAccessoryNotification(mainBuildId);
        }

        const updates = {};
        keysString.split(',').forEach(key => {
          // The key from accessory bundles is "index||item_key". Main build keys are just "item_key".
          // This logic handles both formats safely.
          const itemKey = key.includes('||') ? key.split('||')[1] : key;
          if (itemKey && itemKey.trim()) {
            updates[itemKey.trim()] = 0;
          }
        });

        if (Object.keys(updates).length > 0) {
          cartItemsContainer.updateMultipleQuantities(updates, event);
        }
        return; // Exit after handling the bundle.
      }
        else if (bundleType === 'accessory-bundle') {
        const keysString = this.dataset.keysToRemove;
        const mainBuildId = this.dataset.mainBuildId; // Used for cleaning up notifications

        if (!keysString) {
          console.error("Accessory Bundle Remove Error: Missing keys to remove.", this.dataset);
          return;
        }

        // This is the confirmation alert you were missing.
        if (!confirm("Are you sure you want to remove this accessory bundle?")) return;

        // Clean up any associated incompatibility notifications from session storage.
        if (mainBuildId) {
          this.cleanupAccessoryNotification(mainBuildId);
        }

        const updates = {};
        keysString.split(',').forEach(key => {
          if (key && key.trim()) updates[key.trim()] = 0;
        });

        if (Object.keys(updates).length > 0) {
          cartItemsContainer.updateMultipleQuantities(updates, event);
        }
        return; // Exit after handling the accessory bundle.
      }
      // --- START: NEW AND IMPROVED LOGIC FOR HUB BUNDLES AND SINGLE ITEMS ---
      let itemKeyToRemove = this.dataset.key || this.closest('.cart-item')?.dataset.key;

      if (!itemKeyToRemove) {
        console.error("CartRemoveButton Error: Could not find the item's key to remove.");
        return;
      }

      try {
        const res = await fetch('/cart.js');
        if (!res.ok) throw new Error('Failed to fetch cart state');
        const cart = await res.json();
        
        const itemToRemoveData = cart.items.find(item => item.key === itemKeyToRemove);
        let updates = {};

        // Check if the item is part of our V3 Hub/Freehub bundle
        if (itemToRemoveData?.properties?._bundle_id) {
          const bundleId = itemToRemoveData.properties._bundle_id;
          if (confirm("This item is part of a hub/freehub bundle. Do you want to remove the entire bundle?")) {
            cart.items.forEach(item => {
              if (item.properties?._bundle_id === bundleId) {
                updates[item.key] = 0;
              }
            });
          } else {
            return; // User cancelled, do nothing
          }
        } else {
          // It's just a regular single item, not part of any bundle we handle here
          updates[itemKeyToRemove] = 0;
        }

        if (Object.keys(updates).length > 0) {
          cartItemsContainer.updateMultipleQuantities(updates, event);
        }

      } catch (error) {
        console.error('Error during item removal:', error);
        // Fallback to removing just the single clicked item if the fetch/logic fails
        cartItemsContainer.updateMultipleQuantities({ [itemKeyToRemove]: 0 }, event);
      }
      // --- END: NEW AND IMPROVED LOGIC ---
    });
  }

  /**
   * Removes accessory incompatibility notifications for a specific build from sessionStorage.
   * This ensures they don't reappear after the cart refreshes.
   * @param {string} buildId The main build ID to clear notifications for.
   */
  cleanupAccessoryNotification(buildId) {
    if (!buildId) return;
    const storageKey = 'll_removed_accessories_info';
    const removedInfoString = sessionStorage.getItem(storageKey);

    if (removedInfoString) {
      try {
        let allNotifications = JSON.parse(removedInfoString);
        const updatedNotifications = allNotifications.filter(
          notification => notification.buildIdForNotification !== buildId
        );

        if (updatedNotifications.length > 0) {
          sessionStorage.setItem(storageKey, JSON.stringify(updatedNotifications));
        } else {
          sessionStorage.removeItem(storageKey);
        }
        
        const notificationContainer = document.getElementById('cart-level-accessory-notification');
        if (notificationContainer) {
            displayCartLevelAccessoryNotification();
        }

      } catch (e) {
        console.error("Error cleaning up accessory notification:", e);
        sessionStorage.removeItem(storageKey);
      }
    }
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');
    const debouncedOnChange = debounce((event) => { this.onChange(event); }, ON_CHANGE_DEBOUNCE_TIMER);
    this.addEventListener('change', debouncedOnChange.bind(this));
    this.isRevalidating = false; // Lock to prevent multiple revalidations at once
  }

  // --- START: NEW FREEHUB JANITOR METHOD ---
/**
 * LoamLabs Custom Bicycle Wheel Builder - Cart Janitor Function for Freehubs
 * Identifies and removes "Component Freehub Products" from the cart if their
 * corresponding "Base Hub" is not present, based on shared _bundle_id.
 *
 * @param {Array<Object>} currentCartItems - The current items array from the Shopify cart object.
 * @returns {Promise<boolean>} A promise that resolves to true if cart updates were initiated, false otherwise.
 */
async cleanOrphanedFreehubs(currentCartItems) {
  console.log("[Freehub Janitor] Starting check for orphaned freehubs.");
  const FREEHUB_TAG = 'component:freehub';
  const HUB_TAG = 'component:hub';
  const BUNDLE_ID_PROPERTY = '_bundle_id';
  let cartUpdatesInitiated = false;

  if (!currentCartItems || currentCartItems.length === 0) {
    console.log("[Freehub Janitor] Cart is empty, no freehubs to check.");
    return false;
  }

  const freehubsInCart = currentCartItems.filter(item =>
    // MODIFIED LINE START: Added check for item.tags being an array
    item.tags && Array.isArray(item.tags) && item.tags.includes(FREEHUB_TAG) && item.properties && item.properties[BUNDLE_ID_PROPERTY]
    // MODIFIED LINE END
  );

  const hubsInCart = currentCartItems.filter(item =>
    // MODIFIED LINE START: Added check for item.tags being an array
    item.tags && Array.isArray(item.tags) && item.tags.includes(HUB_TAG) && item.properties && item.properties[BUNDLE_ID_PROPERTY]
    // MODIFIED LINE END
  );

  const itemsToRemove = {}; // Use object for updates compatible with updateMultipleQuantities
  const bundleIdsWithHubs = new Set(hubsInCart.map(hub => hub.properties[BUNDLE_ID_PROPERTY]));

  freehubsInCart.forEach(freehub => {
    const freehubBundleId = freehub.properties[BUNDLE_ID_PROPERTY];

    // If the freehub's bundle ID does not correspond to any hub's bundle ID
    if (!freehubBundleId || !bundleIdsWithHubs.has(freehubBundleId)) {
      console.warn(`[Freehub Janitor] Orphaned freehub found (ID: ${freehub.id}, Key: ${freehub.key}, Bundle ID: ${freehubBundleId}). Marking for removal.`);
      itemsToRemove[freehub.key] = 0; // Set quantity to 0 to remove
    }
  });

  if (Object.keys(itemsToRemove).length > 0) {
    console.log('[Freehub Janitor] Found orphaned freehubs. Preparing to remove:', itemsToRemove);
    // Use the existing updateMultipleQuantities method to remove items
    // Pass a custom source to avoid infinite loops if this triggers onCartUpdate.
    await this.updateMultipleQuantities(itemsToRemove, null, 'freehub-janitor-update');
    cartUpdatesInitiated = true;
    console.log('[Freehub Janitor] Update for orphaned freehubs initiated.');
  } else {
    console.log('[Freehub Janitor] No orphaned freehubs found in cart.');
  }
  return cartUpdatesInitiated;
}
// --- END: NEW FREEHUB JANITOR METHOD ---

  async onChange(event) {
  // Find the specific cart item row that was changed.
  const cartItem = event.target.closest('.cart-item');
  if (!cartItem) return;

  // Get the unique key for the item and the new quantity from the input.
  const itemKey = cartItem.dataset.key;
  const newQuantity = Number(event.target.value);

  // We need to fetch the latest cart state to check the item's properties.
  try {
    const res = await fetch(`${routes.cart_url}.js`);
    if (!res.ok) return;
    
    const cart = await res.json();
    const lineItemData = cart.items.find(item => item.key === itemKey);

    // Check if this item is part of a hub/freehub bundle.
    if (lineItemData?.properties?._bundle_id?.startsWith('hub-bundle-')) {
      const bundleId = lineItemData.properties._bundle_id;
      console.log(`Hub bundle quantity change detected for bundle: ${bundleId}. New quantity: ${newQuantity}`);
      
      // Prepare to update all items with the same bundle_id.
      const updates = {};
      cart.items.forEach(item => {
        if (item.properties._bundle_id === bundleId) {
          updates[item.key] = newQuantity;
        }
      });

      // Use the existing function to send the multi-item update to Shopify.
      this.updateMultipleQuantities(updates, event);
    } else {
      // If it's not our hub bundle, we let the default Shopify update logic handle it.
      // This call handles single items, the main wheel builder, and accessory bundles.
      this.updateMultipleQuantities({ [itemKey]: newQuantity }, event);
    }
  } catch (error) {
    console.error("Error during onChange quantity synchronization:", error);
  }
}

  cartUpdateUnsubscriber = undefined;

  async connectedCallback() {
    console.log("[CartItems.connectedCallback] Initializing...");

    if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
    }
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
    console.log("[CartItems.connectedCallback] Subscribed to PUB_SUB_EVENTS.cartUpdate.");

    if (this.isRevalidating) {
        console.log("[CartItems.connectedCallback] Exiting: A revalidation/update process appears to be active (isRevalidating is true).");
        return;
    }
    
    this.isRevalidating = true; 
    console.log("[CartItems.connectedCallback] Lock acquired by connectedCallback. isRevalidating = true.");
    let revalidationTriggeredCartUpdate = false; // Tracks if accessory revalidation triggers an update

    try {
        const cartResponse = await fetch(`${routes.cart_url}.js`);
        if (cartResponse.ok) {
            let currentCartState = await cartResponse.json(); // Use let as it might be updated

            if (currentCartState.items && currentCartState.items.length > 0) {
                // 1. Run Accessory Revalidation
                const validationResult = await revalidateAccessoryBundlesAgainstMainBuilds(currentCartState.items);
                if (validationResult.cartUpdated && Object.keys(validationResult.updates).length > 0) {
                    console.log("[CartItems.connectedCallback] Accessory revalidation resulted in changes. Updating quantities...");
                    await this.updateMultipleQuantities(validationResult.updates, null, 'cart-items-revalidation-update'); // Pass custom source
                    revalidationTriggeredCartUpdate = true; // Mark that an update was triggered
                } else {
                    console.log("[CartItems.connectedCallback] Accessory revalidation complete, no cart updates needed.");
                }

                // --- START: FREEHUB JANITOR EXECUTION IN CONNECTEDCALLBACK ---
                // If accessory revalidation triggered an update, the next onCartUpdate will handle the janitor.
                // Otherwise, run it now with the current cart state.
                if (!revalidationTriggeredCartUpdate) {
                    const freehubJanitorTriggered = await this.cleanOrphanedFreehubs(currentCartState.items);
                    if (freehubJanitorTriggered) {
                        revalidationTriggeredCartUpdate = true; // Set to true to prevent immediate rendering
                    }
                }
                // --- END: FREEHUB JANITOR EXECUTION IN CONNECTEDCALLBACK ---

            } else { 
                console.log("[CartItems.connectedCallback] Cart is empty, skipping revalidations/janitor."); 
            }
        } else { 
            console.error("[CartItems.connectedCallback] Failed to fetch cart for initial revalidation checks."); 
        }
    } catch (e) {
        console.error("[CartItems.connectedCallback] Error during initial revalidation processes:", e);
    } finally {
        this.isRevalidating = false; 
        console.log("[CartItems.connectedCallback] Lock released by connectedCallback in finally. isRevalidating = false.");
    }

    // Only proceed to render/display notifications if no revalidation/janitor triggered an API update.
    // If an update was triggered, onCartUpdate will handle the subsequent rendering.
    if (!revalidationTriggeredCartUpdate) {
        console.log("[CartItems.connectedCallback] No revalidation/janitor-triggered update. Proceeding with initial renderCustomComponents and notification display.");
        this.renderCustomComponents(); 
        displayCartLevelAccessoryNotification();
    }
}

  async renderCustomComponents() {
    console.log("Cart.js: renderCustomComponents START");

    // Attempt to clear previous loading states more aggressively
    this.querySelectorAll('.custom-build-components, .custom-accessory-bundle-components').forEach(container => {
        if (container.innerHTML.includes("Loading...")) {
            // console.log(`[RCC Pre-Clear] Clearing loading message for: ${container.id || container.className}`);
            // container.innerHTML = ""; // Consider if clearing here is best or handled by section replacement
        }
    });

    let currentCartState = null;
    try {
        console.log("[CartItems.renderCustomComponents] Fetching fresh cart state...");
        const response = await fetch(`${routes.cart_url}.js`);
        if (!response.ok) {
            console.error("[CartItems.renderCustomComponents] Failed to fetch cart state. Status:", response.status);
            throw new Error("Failed to fetch cart state");
        }
        currentCartState = await response.json();
        console.log("[CartItems.renderCustomComponents] Fresh cart state fetched.");
    } catch (e) {
        console.error("Error fetching current cart state in renderCustomComponents:", e);


      
        this.querySelectorAll('.custom-build-components, .custom-accessory-bundle-components').forEach(container => {
            container.innerHTML = "<p>Error loading cart data. Please refresh.</p>";
        });
        return; // Stop if we can't get cart data
    }

    // --- START: Call placeholder update FIRST ---
    let anApiUpdateWasAttemptedByPlaceholders = false;
    if (typeof updateAccessoryBundlePlaceholders === 'function') {
        console.log("[CartItems.renderCustomComponents] Calling updateAccessoryBundlePlaceholders...");
        try {
            anApiUpdateWasAttemptedByPlaceholders = await updateAccessoryBundlePlaceholders(currentCartState.items || [], this);
        } catch (e) {
            console.error("[CartItems.renderCustomComponents] Error during updateAccessoryBundlePlaceholders call:", e);
            // Decide if you want to proceed or stop if placeholder update fails
        }

        if (anApiUpdateWasAttemptedByPlaceholders) {
            console.log("[CartItems.renderCustomComponents] updateAccessoryBundlePlaceholders attempted an API update. This render pass will stop. The subsequent onCartUpdate will handle the final UI.");
            // If an API call was made by updateAccessoryBundlePlaceholders,
            // that call will (or should) trigger PUB_SUB_EVENTS.cartUpdate.
            // The onCartUpdate handler will then call renderCustomComponents again
            // with the truly latest cart state from Shopify.
            return; // Exit this instance of renderCustomComponents
        } else {
            console.log("[CartItems.renderCustomComponents] updateAccessoryBundlePlaceholders made no API calls, or placeholders were already in sync.");
        }
    } else {
        console.warn("[CartItems.renderCustomComponents] updateAccessoryBundlePlaceholders function not found.");
    }
    // --- END: Call placeholder update ---

    // If we reach here, it means updateAccessoryBundlePlaceholders either didn't run,
    // or it ran and determined no API calls were needed (placeholders were in sync).
    // So, we can proceed to render the component details based on the currentCartState.

    console.log("[CartItems.renderCustomComponents] Proceeding to render main build and accessory details.");

    if (!currentCartState || !currentCartState.items || currentCartState.items.length === 0) {
        console.log("[CartItems.renderCustomComponents] Cart is empty or no items after placeholder check. Clearing component displays.");
        this.querySelectorAll('.custom-build-components, .custom-accessory-bundle-components').forEach(container => {
            container.innerHTML = ""; // Clear if cart is empty
        });
        // No need to call updateAccessoryBundlePlaceholders again here if cart is truly empty.
        // Call notification display here because it might be the final render pass for an empty cart.
        displayCartLevelAccessoryNotification();
        console.log("Cart.js: renderCustomComponents END (for empty cart)");
        return;
    }

    this.querySelectorAll('.custom-build-components').forEach(container => {
        const buildMap = container.dataset.buildMap;
        if (buildMap && typeof fetchAndDisplayCustomBuildComponents === 'function') {
           fetchAndDisplayCustomBuildComponents(buildMap, container);
        } else if (buildMap) {
            console.error("[CartItems.renderCustomComponents] fetchAndDisplayCustomBuildComponents function missing.");
            container.innerHTML = "<p>Error: Main build display function missing.</p>";
        }
    });

    this.querySelectorAll('.custom-accessory-bundle-components').forEach(container => {
        const placeholderKey = container.dataset.placeholderKey;
        const accessoryPlaceholderItem = currentCartState.items.find(item =>
            item.key === placeholderKey && item.properties?._is_custom_accessories_bundle === "true" && item.quantity > 0
        );
        if (accessoryPlaceholderItem && typeof fetchAndDisplayAccessoryBundleComponents === 'function') {
            fetchAndDisplayAccessoryBundleComponents(accessoryPlaceholderItem, currentCartState.items, container);
        } else if (accessoryPlaceholderItem) {
            console.error("[CartItems.renderCustomComponents] fetchAndDisplayAccessoryBundleComponents function missing.");
             container.innerHTML = "<p>Error: Accessory display function missing.</p>";
        } else if (placeholderKey) {
            container.innerHTML = ""; // Or "<p>This accessory bundle is empty.</p>"
        }
    });

// --- START: FINAL JAVASCRIPT PRICE CALCULATION ---
try {
  // Find all the main wheel build placeholder rows using the new unique data attribute
  const bundlePlaceholderRows = this.querySelectorAll('tr.cart-item[data-build-id]');
  
  if (bundlePlaceholderRows.length > 0 && currentCartState?.items) {
    bundlePlaceholderRows.forEach(row => {
      const buildIdFromDOM = row.dataset.buildId;
      if (!buildIdFromDOM) return;

      let calculatedTotalPriceCents = 0;
      
      // Loop through the entire cart state from the API
      currentCartState.items.forEach(item => {
        // Find all items that belong to this specific build using the correct property
        if (item.properties?._BuildID === buildIdFromDOM) {
          // Add the price of the component to the total. `final_line_price` is price * quantity.
          calculatedTotalPriceCents += item.final_line_price;
        }
      });
      
      const formattedPrice = formatPrice(calculatedTotalPriceCents);
      const mobilePriceElement = document.getElementById(`bundle-total-price-${buildIdFromDOM}`);
      const desktopPriceElement = document.getElementById(`bundle-total-price-${buildIdFromDOM}-desktop`);

      if (mobilePriceElement) { mobilePriceElement.innerHTML = formattedPrice; }
      if (desktopPriceElement) { desktopPriceElement.innerHTML = formattedPrice; }
    });
  }
} catch(e) {
  console.error("Error during corrected final JS price calculation:", e);
}
// --- END: FINAL JAVASCRIPT PRICE CALCULATION ---

// Display notifications now that custom components have been rendered (or attempted)
// START: New logic to show/hide the "Add Accessories" button
try {
  const mainBuildPlaceholders = currentCartState.items.filter(
    (item) => item.properties?._is_custom_wheel_build === "true"
  );

  mainBuildPlaceholders.forEach((placeholder) => {
    const buildId = placeholder.properties._BuildID;
    if (!buildId) return;

    const hasAccessoryBundle = currentCartState.items.some(
      (item) => item.properties?._build_id === `${buildId}-ACC`
    );

    const addButton = document.getElementById(`AddAccessories-${buildId}`);
    if (addButton) {
      addButton.style.display = hasAccessoryBundle ? "none" : ""; // Show if NO bundle exists
    }
  });
} catch (e) {
  console.error("Error managing 'Add Accessories' button visibility:", e);
}
// END: New logic to show/hide the "Add Accessories" button
displayCartLevelAccessoryNotification();

    console.log("Cart.js: renderCustomComponents END (full render pass)");
}

  async onCartUpdate(event) {
    console.log(`[CartItems.onCartUpdate] Triggered. Source: '${event?.source}'. Current isRevalidating state (at entry): ${this.isRevalidating}`);

    const isFromSelfRevalidation = event?.source === 'cart-items-revalidation-update';
    const isFromPlaceholderInternalUpdate = event?.source === 'UpdateAccPlaceholdersInternal';
    const isFromFreehubJanitor = event?.source === 'freehub-janitor-update'; // New source check for freehub janitor
    const isInternalUpdateSource = isFromSelfRevalidation || isFromPlaceholderInternalUpdate || isFromFreehubJanitor;

    if (this.isRevalidating && !isInternalUpdateSource) {
        console.warn(`[CartItems.onCartUpdate] Exiting: Lock 'isRevalidating' is true AND source ('${event?.source}') is external. An internal update process is likely active.`);
        return;
    }

    if (!isInternalUpdateSource) {
        this.isRevalidating = true;
        console.log("[CartItems.onCartUpdate] Lock acquired for external update processing.");
    }

    try {
        let cartUpdatesInitiated = false; // Track if any internal update triggers

        // Always fetch the freshest cart state first for revalidation/janitor
        const cartResponse = await fetch(`${routes.cart_url}.js`);
        if (!cartResponse.ok) throw new Error("Failed to fetch cart state for update processing.");
        let currentCartState = await cartResponse.json(); // Use let as it might be updated

        if (currentCartState.items && currentCartState.items.length > 0) {
            // 1. Run Accessory Revalidation
            const validationResult = await revalidateAccessoryBundlesAgainstMainBuilds(currentCartState.items);
            if (validationResult.cartUpdated && Object.keys(validationResult.updates).length > 0) {
                console.log("[CartItems.onCartUpdate] Accessory revalidation (due to external source) found changes. Calling updateMultipleQuantities.");
                await this.updateMultipleQuantities(validationResult.updates, event, 'cart-items-revalidation-update'); // Pass custom source
                cartUpdatesInitiated = true;
            }

            // --- START: FREEHUB JANITOR EXECUTION IN ONCARTUPDATE ---
            // If accessory revalidation just triggered an update, re-fetch cart state
            // to ensure freehub janitor operates on the absolute latest cart state.
            if (cartUpdatesInitiated) {
                const freshCartRes = await fetch(`${routes.cart_url}.js`);
                if (freshCartRes.ok) currentCartState = await freshCartRes.json();
            }
            
            // 2. Run Freehub Janitor
            const freehubJanitorTriggered = await this.cleanOrphanedFreehubs(currentCartState.items);
            if (freehubJanitorTriggered) {
                console.log("[CartItems.onCartUpdate] Freehub janitor (due to external source/accessory update) found changes. Cart update already triggered.");
                cartUpdatesInitiated = true; // Mark that an update was initiated by janitor
            }
            // --- END: FREEHUB JANITOR EXECUTION IN ONCARTUPDATE ---

        }
        
        // --- THIS IS THE MAIN RENDERING PATH ---
        // It's reached if:
        // 1. No internal cart updates were initiated (neither accessory revalidation nor freehub janitor).
        // 2. The *current* onCartUpdate call was triggered by an internal source (e.g., freehub janitor just ran and now it's rendering its result).
        if (!cartUpdatesInitiated || isInternalUpdateSource) {
            console.log("[CartItems.onCartUpdate] Proceeding to the main rendering path.");
            this.enableLoading();

            const sectionToFetch = this.tagName === 'CART-DRAWER-ITEMS' ? 'cart-drawer' : 'main-cart-items';
            let fetchUrl = `${routes.cart_url}?section_id=${sectionToFetch}`;
            
            const response = await fetch(fetchUrl);
            if (!response.ok) throw new Error(`Section fetch failed with status ${response.status}`);
            
            const responseText = await response.text();
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceContent = html.querySelector(this.tagName === 'CART-DRAWER-ITEMS' ? '.drawer__inner' : '.js-contents');
            const targetContent = this.tagName === 'CART-DRAWER-ITEMS' ? document.querySelector('.drawer__inner') : document.querySelector(`#main-cart-items .js-contents`);

            if (sourceContent && targetContent) {
                targetContent.innerHTML = sourceContent.innerHTML;
                console.log("[CartItems.onCartUpdate] Main cart/drawer DOM content has been replaced with the latest from Shopify.");
            } else {
                console.warn("[CartItems.onCartUpdate] Could not find source or target content for section replacement.");
            }

            if (typeof this.renderCustomComponents === 'function') {
                await this.renderCustomComponents();
            }
        } else {
            console.log("[CartItems.onCartUpdate] Internal cart updates were initiated. Waiting for next onCartUpdate cycle to render.");
        }

    } catch (e) {
        console.error("Error during onCartUpdate:", e);
    } finally {
        this.disableLoading();
        // Release the lock only if it was acquired by this instance (for external updates)
        // AND no *further* internal updates were initiated within this cycle.
        // If 'cartUpdatesInitiated' is true, it means another onCartUpdate will be coming,
        // so we don't release the lock prematurely.
        if (!isInternalUpdateSource && !cartUpdatesInitiated) {
            this.isRevalidating = false;
            console.log("[CartItems.onCartUpdate] Lock released after external update processing.");
        }
        console.log("[CartItems.onCartUpdate] onCartUpdate instance finished.");
    }
}

  getSectionsToRender() {
    const sections = [];
    const mainCartItemsEl = document.getElementById('main-cart-items');
    if (mainCartItemsEl && mainCartItemsEl.dataset.id) {
        sections.push({
            id: 'main-cart-items',
            section: mainCartItemsEl.dataset.id,
            selector: '.js-contents',
        });
    } else {
        // Fallback or default if data-id isn't present for some reason
        sections.push({ id: 'main-cart-items', section: 'main-cart-items', selector: '.js-contents'});
    }

    sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    sections.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });

    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter && cartFooter.dataset.id) {
      sections.push({ id: 'main-cart-footer', section: cartFooter.dataset.id, selector: '.js-contents' });
    } else {
      sections.push({ id: 'main-cart-footer', section: 'main-cart-footer', selector: '.js-contents' });
    }
    
    return sections.filter(section => typeof section.section !== 'undefined' && section.section !== null);
  }
  
    async updateMultipleQuantities(updates, event, source = 'default-update') { // Added source parameter
    this.enableLoading();
    const sectionsToRenderConfig = this.getSectionsToRender();
    const sectionIdsToSend = sectionsToRenderConfig.map((s) => s.section).filter(Boolean);

    if (sectionIdsToSend.length === 0) {
        console.warn("Cart.js: updateMultipleQuantities - No valid section IDs to send. Aborting.");
        this.disableLoading();
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = "An error occurred (no sections).";
        return;
    }

    const body = JSON.stringify({
      updates: updates,
      sections: sectionIdsToSend,
      sections_url: window.location.pathname,
    });

    try {
        const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
        const stateText = await response.text();
        const parsedState = JSON.parse(stateText);

        // --- ADDED ROBUSTNESS CHECK ---
        if (!parsedState.sections) {
            console.error('Cart update API call did not return sections. Response:', parsedState);
            const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
            if (errors) {
                // Use the error message from Shopify if available, otherwise show a generic one.
                errors.textContent = parsedState.description || parsedState.message || window.cartStrings.error;
            }
            this.disableLoading();
            return; // Stop execution to prevent a crash.
        }
        // --- END OF ADDED CHECK ---

        CartPerformance.measure("multiple-update:paint-updated-sections", () => {
          sectionsToRenderConfig.forEach((sectionConfig) => {
            const sectionHTMLFromState = parsedState.sections[sectionConfig.section];
            if (sectionHTMLFromState) {
                const elementToReplace = document.getElementById(sectionConfig.id)?.querySelector(sectionConfig.selector) || document.getElementById(sectionConfig.id);
                if (elementToReplace) {
                    elementToReplace.innerHTML = this.getSectionInnerHTML(sectionHTMLFromState, sectionConfig.selector);
                } else {
                    // console.warn("Cart.js: updateMultipleQuantities - Element to replace NOT FOUND for section id:", sectionConfig.id);
                }
            } else {
                // console.warn("Cart.js: updateMultipleQuantities - No HTML returned for section:", sectionConfig.section);
            }
          });
          this.updateLiveRegions(null, window.cartStrings.success); 
          // Pass the source to the publish event
          publish(PUB_SUB_EVENTS.cartUpdate, { source: source, cartData: parsedState, generatedByRevalidation: true }); 
        });
        if(event) CartPerformance.measureFromEvent("multiple-update:user-action", event);

    } catch (e) {
        console.error('Error updating multiple cart items:', e);
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
    } finally {
        this.disableLoading();
    }
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', true);
    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (cartStatus) cartStatus.setAttribute('aria-hidden', false);
    setTimeout(() => { if (cartStatus) cartStatus.setAttribute('aria-hidden', true); }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    if (selector === '.shopify-section') {
        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        const sectionWrapper = parsedDoc.body.firstChild;
        return sectionWrapper ? sectionWrapper.innerHTML : '';
    }
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (element) return element.innerHTML;
    return '';
  }

  enableLoading() {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', true);
  }
}
customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('input', debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}